import {
  MEDIA_LARGE,
  MEDIA_MEDIUM,
  MEDIA_SMALL,
  MEDIA_XLARGE,
  MEDIA_XSMALL
} from 'constants/screenResolutions';
import styled from 'styled-components';
import { transparentize } from 'utils/color';
import {
  BEYOND_MAX_RESOLUTION,
  CARD_DIMENSION,
  CARD_SIZE_MAP,
  FlipCardsGridPositioning,
  GAP_SIZE,
  MARGIN_SIZE,
  TEXT_COUNT_FONT_SIZES_MAP,
  TEXT_STOP_POINTS
} from './types';

type Props = {
  alignment: string;
  rowSize: number;
  cardDimension: number;
  theme?: any;
};

const getCardDimensionAndSpacing = (originalDimension: number, rowSize: number) => {
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;
  let cardDimension = originalDimension;

  if (windowWidth < MEDIA_SMALL && windowWidth < windowHeight) {
    cardDimension = CARD_DIMENSION.MEDIUM;
  } else if (
    (windowWidth > MEDIA_XSMALL && rowSize < 2) ||
    (windowWidth > MEDIA_MEDIUM && rowSize < 3)
  ) {
    cardDimension = CARD_DIMENSION.LARGE;
  } else if (
    windowWidth > MEDIA_MEDIUM &&
    windowWidth <= MEDIA_XLARGE &&
    cardDimension === CARD_DIMENSION.MEDIUM
  ) {
    cardDimension = CARD_DIMENSION.SMALL;
  } else if (windowWidth < MEDIA_XSMALL) {
    cardDimension = CARD_DIMENSION.MEDIUM;
  } else if (windowWidth <= MEDIA_MEDIUM && rowSize > 1) {
    cardDimension = CARD_DIMENSION.MEDIUM;
  }
  return cardDimension;
};

const getCardGapSize = (cardDimension: number) => {
  const windowWidth = window.innerWidth;
  if (windowWidth <= MEDIA_XSMALL) {
    return GAP_SIZE[MEDIA_XSMALL][cardDimension];
  }
  if (windowWidth <= MEDIA_SMALL) {
    return GAP_SIZE[MEDIA_SMALL][cardDimension];
  }
  if (windowWidth <= MEDIA_MEDIUM) {
    return GAP_SIZE[MEDIA_MEDIUM][cardDimension];
  }
  if (windowWidth <= MEDIA_LARGE) {
    return GAP_SIZE[MEDIA_LARGE][cardDimension];
  }
  if (windowWidth <= MEDIA_XLARGE) {
    return GAP_SIZE[MEDIA_XLARGE][cardDimension];
  }
  return GAP_SIZE[BEYOND_MAX_RESOLUTION][cardDimension];
};

export const Container = styled.div<Props>`
  ${(props: any) => {
    const cardDimension = getCardDimensionAndSpacing(props.cardDimension, props.rowSize);
    const gap = getCardGapSize(props.cardDimension);
    const totalMarginBottom = window.innerWidth >= MEDIA_MEDIUM ? 61 : 51;

    return `
      display: flex;
      align-items: center;
      justify-content: ${FlipCardsGridPositioning[props.alignment]};
      width: 100%;
      
      [data-type*='flipCards'].eg-content-editor {
        width: 100%;
        display: flex;
        flex-flow: row wrap;
        align-content: flex-start;
        gap: ${gap}px;
        justify-content: ${FlipCardsGridPositioning[props.alignment]};
        font-family: ${props.theme.fonts.secondaryFont.fontFamily};
        max-width: calc(${cardDimension}px * ${props.rowSize} + ${gap}px * ${props.rowSize - 1});
        z-index: 2;
        margin-bottom: calc(23px - (${totalMarginBottom}px - ${gap}px));
      }
      [data-type*='oneImage'].eg-content-editor  {
        .column:nth-child(1):nth-last-child(2),
        .column,
        & {
          height: 100%
        }
      }
      [data-type*='flipCards'].eg-content-editor > .column:nth-child(1):nth-last-child(2),
      [data-type*='flipCards'].eg-content-editor > .column {
        width: min(${cardDimension}px, 75vw) !important;
        height: min(${cardDimension}px, 75vw) !important;
        padding: 0;
        margin-bottom: calc(${totalMarginBottom}px - ${gap}px);

        ${Object.values(TEXT_STOP_POINTS).map(
          POINT => `.stop-point-${POINT} {
            font-size: ${TEXT_COUNT_FONT_SIZES_MAP[POINT][CARD_SIZE_MAP[cardDimension]].fontSize}px;
            line-height: ${
              TEXT_COUNT_FONT_SIZES_MAP[POINT][CARD_SIZE_MAP[cardDimension]].lineHeight
            };
          }`
        )}

        .row {
          width: 100%;
          height: 100%;
        }
        p {
          &:not(:last-child) {
            margin-bottom: 0;
          }
        }
        p:nth-child(1n) {
          margin: 0 ${MARGIN_SIZE[cardDimension]}px;
          box-sizing: border-box;
        }
        p:first-child {
          margin-top: auto;
        }
        p:last-child {
          margin-bottom: auto;
        }
      }

      .row.flip .flip-card-wrapper {
        transform: rotateY(180deg);
        box-shadow: none;        
        :hover {
          box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.1);
        }
        .flip-card-back-side {
          visibility: visible;
        }
      }

      .flip-card-wrapper,
      .flip-card-front-side,
      .flip-card-back-side {
        box-sizing: border-box;
        width: 100%;
        height: 100%;
      }

      .flip-card-wrapper p {
        font-weight: 600;
        text-align: center;
        color: ${props.theme.colors.textColor};
      }
      
      .flip-card-front-side {
        transform: rotateY(0deg);
        border-top: 2px ${props.theme.colors.mainColor} solid;
        border-bottom: 2px white solid;
      }
      
      .flip-card-front-side[data-front-side-content-type='image-content'] {
        border-top: none;
      }

      
      .flip-card-wrapper {
        transition: 0.6s;
        transform-style: preserve-3d;
        position: relative;
        cursor: pointer;
        box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.1);
        
        :hover {
          box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.1);
        }
      }

      .flip-card-front-side,
      .flip-card-back-side {
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;
        overflow: hidden auto;
        &::-webkit-scrollbar {
          width: 8px;
          background-color: white;
        }
        &::-webkit-scrollbar-thumb {
          margin-right: 8px;
          background-color: ${transparentize(`${props.theme.colors.textColor}`, 0.2)};
          border-radius: 6px;
        }
        
        position: absolute;

        display: flex;
        flex-direction: column;
      }
      
      .flip-card-back-side {
        transform: rotateY(180deg);
        border: 4px ${transparentize(props.theme.colors.textColor, 0.08)} solid;
        visibility: hidden;
        transition: 0.6s ease-out;
      }
      .flip-button {
        cursor: pointer;

        right: 0;
        position: absolute;
        width: fit-content;
        padding-top: 8px;

        font-size: 11px;
        color: ${transparentize(props.theme.colors.textColor, 0.7)};

        display: flex;
        align-items: center;
        gap: 8px;
      }
    `;
  }}
`;

export const FlipButton = styled.div``;
